import { useState, ChangeEvent, KeyboardEvent, useEffect } from 'react';

import type { NextPage } from 'next';

import { useForm, SubmitHandler } from 'react-hook-form';

import { useCompanyResetPassword } from '@hooks_iqfu/dashboard/useCompanyResetPassword';

import RefTextInput from '@components_iqfu/dashboard/form/ref_text_input/RefTextInput';

import { emailRegex } from '@constants_common';
import { ValidationText, NotificationText } from '@constants_iqfu';
import { useNotificationContext } from '@contexts_common/notificationContext';
import styles from '@styles_iqfu/dashboard/company_password_reset.module.scss';
import { handleInputText } from '@utils_common/handleInputText';
import { showInputTextMaxLength } from '@utils_common/showInputTextLimitLength';

type Inputs = {
  email: string;
};

const CompanyForgotPassword: NextPage = () => {
  const { requestPasswordResetEmail } = useCompanyResetPassword();
  const { setNotificationDisplay, setNotificationMessage, setNotificationStatus } = useNotificationContext();
  const [email, setEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const {
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<Inputs>({ reValidateMode: 'onBlur', mode: 'onBlur' });

  //デフォルトでエラーをセットしておくことで、送信ボタンを最初のblurのvalidateチェック終わるまで、inactiveにしておく
  useEffect(() => {
    setError('email', { type: 'onBlur', message: '' });
  }, []);

  const onSubmit: SubmitHandler<Inputs> = async () => {
    const { status } = await requestPasswordResetEmail(email);
    if (status === 'success') {
      setNotificationStatus('success');
      setNotificationMessage(NotificationText.successSubmitEmail);
      setResetSuccess(true);
      //local確認URL: http://localhost:3001/letter_opener
    } else if (status === 'emailNotFound') {
      setNotificationStatus('alert');
      setNotificationMessage(NotificationText.emailNotFound);
    } else {
      setNotificationStatus('alert');
      setNotificationMessage(NotificationText.error);
    }
    clearErrors();
    setNotificationDisplay(true);
  };

  return (
    <div className={styles.container}>
      {resetSuccess ? (
        <div className={styles.wrapper}>
          <div className={styles.title}>メール送信完了</div>
          <p>パスワード再設定用のURLをご入力のメールアドレスに送信しました。</p>
          <p>記載された内容に従って、パスワードの再設定を行なってください。</p>
        </div>
      ) : (
        <div className={styles.wrapper}>
          <div className={styles.title}>パスワード再設定</div>
          <div className={styles.text}>ご登録いただいたメールアドレスを入力して下さい。</div>
          <div className={styles.text}>
            メールアドレス宛にパスワード変更ページのURLが記載されたメールを送信いたします。
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.input_item}>
              <label className={styles.input_item_label}>メールアドレス</label>
              <div className={styles.password_form_container}>
                <RefTextInput
                  type="text"
                  value={email}
                  {...register('email', {
                    required: true,
                    maxLength: { value: 255, message: showInputTextMaxLength(255) },
                    pattern: { value: emailRegex, message: ValidationText.invalidEmail },
                  })}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    handleInputText(event, setEmail);
                  }}
                  onKeyPress={(e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key == 'Enter') onSubmit;
                  }}
                />
                {errors.email && <span className={styles.error_text}>{errors.email.message}</span>}
              </div>
            </div>

            <button
              className={`${styles.submit_button} ${isSubmitting || errors.email ? styles.inactive : ''}`}
              type="submit"
            >
              送信
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default CompanyForgotPassword;
